import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"

import { Progress } from "@/components/ui/progress"
import { useParams, useLocation } from "wouter"
import { ArrowRight, ArrowLeft, CheckCircle, Briefcase, Calendar, Target, Building, Plus, X, AlertTriangle, TrendingUp, Users, Star, Trophy, Rocket } from "lucide-react"
import { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useToast } from "@/hooks/use-toast"
import React from "react"
import PillSelector from "@/components/ui/PillSelector"
import { VALUE_ENGINEERING_REVENUE_BANDS } from "@/utils/pricingTierMapper"
import { useValueAssessment } from "@/contexts/ValueAssessmentContext"
// Using text logo instead of image for now
const redwoodMark = null

// Helper function to detect incomplete input screens
function getFirstIncompleteScreen(valueSummary: any): string {
  if (!valueSummary?.inputScreens?.projectKickoff?.clientName) return "project-kickoff";
  if (!valueSummary?.inputScreens?.strategicContext?.priorities?.length) return "strategic-context";
  if (!valueSummary?.inputScreens?.resourceAllocation?.totalFTEs) return "resource-allocation";
  return "review";
}

// Helper function to parse URL query parameters
function useQueryParams() {
  const [location] = useLocation();
  const searchParams = new URLSearchParams(location.split('?')[1] || '');
  return {
    screen: searchParams.get('screen'),
    resume: searchParams.get('resume') === 'true',
    projectId: searchParams.get('projectId')
  };
}

// Utility functions for generating deep links
export function generateResumeLink(projectId: string): string {
  return `/value-assessment/input?projectId=${projectId}&resume=true`;
}

export function generateDeepLink(projectId: string, screen: string): string {
  return `/value-assessment/input/${screen}?projectId=${projectId}`;
}

// Export screens for external use
export const INPUT_SCREENS = {
  PROJECT_KICKOFF: 'project-kickoff',
  STRATEGIC_CONTEXT: 'strategic-context', 
  RESOURCE_ALLOCATION: 'resource-allocation',
  REVIEW: 'review'
} as const;

interface PublicInputData {
  projectKickoff?: any
  strategicContext?: any
  fteAllocation?: any
  inputSubmitted?: boolean
  submissionDate?: string
}

interface ToolData {
  name: string
  licenseCost?: number
  serviceCost?: number
  knownIssues?: string
}

interface StrategicContextData {
  painPoints: string[]
  blockers: string[]
  tools: ToolData[]
  currency: string
  otherPainPoints?: string
  otherBlockers?: string
}

const SCREENS = [
  {
    id: 'kickoff',
    title: 'Project Kickoff',
    description: 'Basic project information and strategic goals',
    step: 1
  },
  {
    id: 'context',
    title: 'Strategic Context',
    description: 'Pain points, blockers, and current tools',
    step: 2
  },
  {
    id: 'allocation',
    title: 'FTE Allocation',
    description: 'Allocate FTEs to R2R activities by region',
    step: 3
  },
  {
    id: 'complete',
    title: 'Submission Complete',
    description: 'Thank you for providing your information',
    step: 4
  }
]

const goalOptions = [
  "IPO Readiness",
  "M&A Integration", 
  "Regulatory Compliance",
  "Audit Readiness",
  "ESG Reporting",
  "Financial Visibility",
  "Faster Close",
  "Process Automation",
  "Data Integrity",
  "Scale Finance",
  "Risk Reduction",
  "Reduce Manual Work"
]

const erpOptions = ["SAP ECC", "SAP S/4HANA", "Oracle EBS", "Oracle Cloud", "Workday", "NetSuite", "Other"]

const painPointOptions = [
  "Manual journal entry prep",
  "Delay in intercompany matching", 
  "High post-close adjustments",
  "Late reconciliations",
  "Lack of orchestration",
  "Approvals via email",
  "Lack of real-time visibility",
  "Workload spikes at month-end",
  "No ownership or accountability"
]

const blockerOptions = [
  "Budget constraints",
  "Technical limitations",
  "Resistance to change",
  "Lack of expertise",
  "Regulatory requirements",
  "System compatibility",
  "Time constraints",
  "Management support"
]

const toolOptions = [
  "BlackLine",
  "Trintech",
  "Excel", 
  "SAP",
  "RPA",
  "Other"
]

const currencyOptions = [
  { value: "USD", label: "USD", symbol: "$" },
  { value: "GBP", label: "GBP", symbol: "£" },
  { value: "EUR", label: "EUR", symbol: "€" },
  { value: "CAD", label: "CAD", symbol: "C$" },
  { value: "AUD", label: "AUD", symbol: "A$" }
]

const R2R_ACTIVITIES = [
  "Balance sheet reconciliation - adjustments",
  "Bank reconciliation",
  "Intercompany reconciliation", 
  "Fixed asset reconciliation",
  "Inventory reconciliation",
  "Standard journal entries",
  "Adjusting journal entries",
  "Reclassification entries",
  "Accrual entries",
  "Revenue accruals",
  "Expense accruals",
  "Payroll accruals",
  "Tax accruals",
  "Intercompany eliminations",
  "Transfer pricing adjustments",
  "Period open/closing",
  "Close checklist management",
  "Close calendar coordination",
  "Variance analysis",
  "Management reporting",
  "Statutory reporting",
  "Consolidation",
  "Financial statement preparation",
  "Budget vs actual analysis",
  "Forecast variance analysis",
  "Prior period adjustments"
]

const REGIONS = [
  { key: 'americas', label: 'North America', defaultCost: 85000 },
  { key: 'emea', label: 'EMEA', defaultCost: 65000 },
  { key: 'apac', label: 'APAC', defaultCost: 45000 },
  { key: 'india', label: 'India', defaultCost: 25000 },
  { key: 'shared_services', label: 'Shared Services', defaultCost: 35000 }
]

// Project Kickoff Screen Component
function ProjectKickoffForm({ data, onChange }: { data: any; onChange: (data: any) => void }) {
  const [formData, setFormData] = useState({
    clientName: '',
    erpSystems: [],
    legalEntities: 0,
    revenueBand: '',
    currentCloseDays: 5,
    targetCloseDays: 3,
    strategicGoals: [],
    regionalFTEs: {
      americas: 0,
      emea: 0,
      apac: 0,
      india: 0,
      shared_services: 0
    },
    fteCostsByRegion: {
      americas: 85000,
      emea: 65000,
      apac: 45000,
      india: 25000,
      shared_services: 35000
    },
    ...data
  })

  const [customGoal, setCustomGoal] = useState("")
  const [sliderValue, setSliderValue] = useState([formData.currentCloseDays])

  useEffect(() => {
    onChange(formData)
  }, [formData, onChange])

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleAddGoal = () => {
    if (customGoal.trim() && !formData.strategicGoals.includes(customGoal.trim())) {
      updateField('strategicGoals', [...formData.strategicGoals, customGoal.trim()])
      setCustomGoal("")
    }
  }

  const handleRemoveGoal = (goalToRemove: string) => {
    updateField('strategicGoals', formData.strategicGoals.filter(goal => goal !== goalToRemove))
  }



  const handleERPChange = (erpSystem: string, isChecked: boolean) => {
    if (isChecked) {
      updateField('erpSystems', [...formData.erpSystems, erpSystem])
    } else {
      updateField('erpSystems', formData.erpSystems.filter(system => system !== erpSystem))
    }
  }

  const totalFTEs = Object.values(formData.regionalFTEs).reduce((sum, count) => sum + count, 0)

  return (
    <div className="space-y-8">
      {/* Basic Information */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Building className="w-5 h-5 text-[#4AE2C9]" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-gray-300">Client Name</Label>
              <Input
                value={formData.clientName}
                onChange={(e) => updateField('clientName', e.target.value)}
                className="bg-[#0F172A] border-[#334155] text-white mt-2"
                placeholder="Enter client organization name"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">Revenue Band</Label>
              <Select value={formData.revenueBand} onValueChange={(value) => updateField('revenueBand', value)}>
                <SelectTrigger className="bg-[#0F172A] border-[#334155] text-white mt-2">
                  <SelectValue placeholder="Select revenue band" />
                </SelectTrigger>
                <SelectContent>
                  {VALUE_ENGINEERING_REVENUE_BANDS.map(band => (
                    <SelectItem key={band.value} value={band.value}>
                      {band.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-gray-300">Legal Entities</Label>
            <Input
              type="number"
              value={formData.legalEntities}
              onChange={(e) => updateField('legalEntities', parseInt(e.target.value) || 0)}
              className="bg-[#0F172A] border-[#334155] text-white mt-2"
              placeholder="Number of legal entities"
            />
          </div>
        </CardContent>
      </Card>

      {/* ERP Systems */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Target className="w-5 h-5 text-[#4AE2C9]" />
            ERP Systems
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {erpOptions.map((erp) => (
              <label key={erp} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.erpSystems.includes(erp)}
                  onChange={(e) => handleERPChange(erp, e.target.checked)}
                  className="rounded border-[#334155] text-[#4AE2C9] focus:ring-[#4AE2C9] focus:ring-offset-0"
                />
                <span className="text-gray-300">{erp}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Close Timeline */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Calendar className="w-5 h-5 text-[#4AE2C9]" />
            Close Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-gray-300">Current Close Days</Label>
              <Input
                type="number"
                min={1}
                max={30}
                value={formData.currentCloseDays === 0 ? "" : formData.currentCloseDays}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0
                  updateField('currentCloseDays', value)
                  setSliderValue([value])
                }}
                onFocus={(e) => {
                  if (e.target.value === "0") {
                    e.target.value = ""
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    updateField('currentCloseDays', 5)
                    setSliderValue([5])
                  }
                }}
                className="bg-[#0F172A] border-[#334155] text-white mt-2"
                placeholder="Enter current close days"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">Target Close Days</Label>
              <Input
                type="number"
                min={1}
                max={30}
                value={formData.targetCloseDays === 0 ? "" : formData.targetCloseDays}
                onChange={(e) => updateField('targetCloseDays', parseInt(e.target.value) || 0)}
                onFocus={(e) => {
                  if (e.target.value === "0") {
                    e.target.value = ""
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    updateField('targetCloseDays', 3)
                  }
                }}
                className="bg-[#0F172A] border-[#334155] text-white mt-2"
                placeholder="Enter target close days"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Strategic Goals */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Target className="w-5 h-5 text-[#4AE2C9]" />
            Strategic Goals
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {goalOptions.map((goal) => (
              <label key={goal} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.strategicGoals.includes(goal)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateField('strategicGoals', [...formData.strategicGoals, goal])
                    } else {
                      updateField('strategicGoals', formData.strategicGoals.filter(g => g !== goal))
                    }
                  }}
                  className="rounded border-[#334155] text-[#4AE2C9] focus:ring-[#4AE2C9] focus:ring-offset-0"
                />
                <span className="text-gray-300">{goal}</span>
              </label>
            ))}
          </div>

          <div className="flex gap-2">
            <Input
              value={customGoal}
              onChange={(e) => setCustomGoal(e.target.value)}
              placeholder="Add custom strategic goal"
              className="bg-[#0F172A] border-[#334155] text-white"
            />
            <Button 
              onClick={handleAddGoal}
              className="bg-[#4AE2C9] text-[#0F172A] hover:bg-[#3bc7aa]"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {formData.strategicGoals.length > 0 && (
            <div className="space-y-2">
              <Label className="text-gray-300">Selected Goals:</Label>
              <div className="flex flex-wrap gap-2">
                {formData.strategicGoals.map((goal) => (
                  <span
                    key={goal}
                    className="bg-[#4AE2C9] text-[#0F172A] px-3 py-1 rounded-full text-sm flex items-center gap-1"
                  >
                    {goal}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => handleRemoveGoal(goal)}
                    />
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Regional FTEs */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Users className="w-5 h-5 text-[#4AE2C9]" />
            Regional FTE Distribution
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REGIONS.map((region) => (
              <div key={region.key}>
                <Label className="text-gray-300">{region.label}</Label>
                <Input
                  type="number"
                  value={formData.regionalFTEs[region.key]}
                  onChange={(e) => updateField('regionalFTEs', {
                    ...formData.regionalFTEs,
                    [region.key]: parseInt(e.target.value) || 0
                  })}
                  className="bg-[#0F172A] border-[#334155] text-white mt-2"
                  placeholder="FTE count"
                />
              </div>
            ))}
          </div>

          <div className="bg-forestBlack p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Total FTEs</span>
              <span className="text-[#4AE2C9] font-bold text-lg">{totalFTEs}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Strategic Context Screen Component
function StrategicContextForm({ data, onChange }: { data: any; onChange: (data: any) => void }) {
  const [formData, setFormData] = useState<StrategicContextData>({
    painPoints: [],
    blockers: [],
    tools: [],
    currency: "USD",
    otherPainPoints: "",
    otherBlockers: "",
    ...data
  })

  useEffect(() => {
    onChange(formData)
  }, [formData, onChange])

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleAddTool = () => {
    updateField('tools', [...formData.tools, { name: '', licenseCost: 0, serviceCost: 0 }])
  }

  const handleUpdateTool = (index: number, field: string, value: any) => {
    const updatedTools = [...formData.tools]
    updatedTools[index] = { ...updatedTools[index], [field]: value }
    updateField('tools', updatedTools)
  }

  const handleRemoveTool = (index: number) => {
    updateField('tools', formData.tools.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-8">
      {/* Pain Points */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <AlertTriangle className="w-5 h-5 text-[#4AE2C9]" />
            Pain Points
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PillSelector
            options={painPointOptions}
            selectedOptions={formData.painPoints}
            onSelectionChange={(selected) => updateField('painPoints', selected)}
            placeholder="Select pain points"
            className="mb-4"
          />
          
          <div className="mt-4">
            <Label className="text-gray-300">Additional Pain Points</Label>
            <Textarea
              value={formData.otherPainPoints}
              onChange={(e) => updateField('otherPainPoints', e.target.value)}
              placeholder="Describe any other pain points..."
              className="bg-[#0F172A] border-[#334155] text-white mt-2"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Implementation Blockers */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <X className="w-5 h-5 text-[#4AE2C9]" />
            Implementation Blockers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PillSelector
            options={blockerOptions}
            selectedOptions={formData.blockers}
            onSelectionChange={(selected) => updateField('blockers', selected)}
            placeholder="Select implementation blockers"
            className="mb-4"
          />
          
          <div className="mt-4">
            <Label className="text-gray-300">Additional Blockers</Label>
            <Textarea
              value={formData.otherBlockers}
              onChange={(e) => updateField('otherBlockers', e.target.value)}
              placeholder="Describe any other blockers..."
              className="bg-[#0F172A] border-[#334155] text-white mt-2"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Current Tools */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <TrendingUp className="w-5 h-5 text-[#4AE2C9]" />
            Current Tools & Platforms
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.tools.map((tool, index) => (
            <div key={index} className="bg-forestBlack p-4 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-gray-300">Tool {index + 1}</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveTool(index)}
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Tool Name</Label>
                  <Select 
                    value={tool.name} 
                    onValueChange={(value) => handleUpdateTool(index, 'name', value)}
                  >
                    <SelectTrigger className="bg-[#0F172A] border-[#334155] text-white mt-2">
                      <SelectValue placeholder="Select tool" />
                    </SelectTrigger>
                    <SelectContent>
                      {toolOptions.map(option => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-gray-300">License Cost</Label>
                  <Input
                    type="number"
                    value={tool.licenseCost}
                    onChange={(e) => handleUpdateTool(index, 'licenseCost', parseInt(e.target.value) || 0)}
                    className="bg-[#0F172A] border-[#334155] text-white mt-2"
                    placeholder="Annual license cost"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Service Cost</Label>
                  <Input
                    type="number"
                    value={tool.serviceCost}
                    onChange={(e) => handleUpdateTool(index, 'serviceCost', parseInt(e.target.value) || 0)}
                    className="bg-[#0F172A] border-[#334155] text-white mt-2"
                    placeholder="Annual service cost"
                  />
                </div>
              </div>
            </div>
          ))}
          
          <Button
            onClick={handleAddTool}
            className="bg-[#4AE2C9] text-[#0F172A] hover:bg-[#3bc7aa]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Tool
          </Button>
        </CardContent>
      </Card>

      {/* Currency */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <TrendingUp className="w-5 h-5 text-[#4AE2C9]" />
            Currency
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={formData.currency} onValueChange={(value) => updateField('currency', value)}>
            <SelectTrigger className="bg-[#0F172A] border-[#334155] text-white">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              {currencyOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label} ({option.symbol})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
    </div>
  )
}

// FTE Allocation Screen Component
function FTEAllocationForm({ data, onChange }: { data: any; onChange: (data: any) => void }) {
  const [allocationMatrix, setAllocationMatrix] = useState(() => {
    const matrix = {}
    R2R_ACTIVITIES.forEach(activity => {
      matrix[activity] = {
        'North America': 0,
        'EMEA': 0,
        'APAC': 0,
        'India': 0,
        'Shared Services': 0
      }
    })
    return { ...matrix, ...data }
  })

  useEffect(() => {
    onChange(allocationMatrix)
  }, [allocationMatrix, onChange])

  const updateAllocation = (activity: string, region: string, value: number) => {
    setAllocationMatrix(prev => ({
      ...prev,
      [activity]: {
        ...prev[activity],
        [region]: value
      }
    }))
  }

  const getRegionTotal = (region: string) => {
    return R2R_ACTIVITIES.reduce((sum, activity) => {
      return sum + (allocationMatrix[activity]?.[region] || 0)
    }, 0)
  }

  const getTotalAllocation = () => {
    return R2R_ACTIVITIES.reduce((sum, activity) => {
      return sum + Object.values(allocationMatrix[activity] || {}).reduce((activitySum, val) => activitySum + val, 0)
    }, 0)
  }

  const getActivityTotal = (activity: string) => {
    return Object.values(allocationMatrix[activity] || {}).reduce((sum, val) => sum + val, 0)
  }

  const totalAllocation = getTotalAllocation()
  const allocationPercentage = totalAllocation > 0 ? (totalAllocation / totalAllocation) * 100 : 0

  return (
    <div className="space-y-8">
      {/* Allocation Summary */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Users className="w-5 h-5 text-[#4AE2C9]" />
            FTE Allocation Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {['North America', 'EMEA', 'APAC', 'India', 'Shared Services'].map(region => (
              <div key={region} className="bg-forestBlack p-4 rounded-lg">
                <div className="text-center">
                  <div className="text-sm text-gray-300">{region}</div>
                  <div className="text-xl font-bold text-[#4AE2C9]">{getRegionTotal(region)}</div>
                  <div className="text-xs text-gray-400">FTEs</div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 bg-forestBlack p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Total Allocated FTEs</span>
              <span className="text-[#4AE2C9] font-bold text-lg">{totalAllocation}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Allocation Matrix */}
      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Target className="w-5 h-5 text-[#4AE2C9]" />
            R2R Activity Allocation Matrix
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[#334155]">
                  <th className="text-left p-3 text-gray-300">Activity</th>
                  <th className="text-center p-3 text-gray-300">North America</th>
                  <th className="text-center p-3 text-gray-300">EMEA</th>
                  <th className="text-center p-3 text-gray-300">APAC</th>
                  <th className="text-center p-3 text-gray-300">India</th>
                  <th className="text-center p-3 text-gray-300">Shared Services</th>
                  <th className="text-center p-3 text-gray-300">Total</th>
                </tr>
              </thead>
              <tbody>
                {R2R_ACTIVITIES.map((activity, index) => (
                  <tr key={activity} className={index % 2 === 0 ? 'bg-forestBlack/30' : ''}>
                    <td className="p-3 text-gray-300 text-sm">{activity}</td>
                    {['North America', 'EMEA', 'APAC', 'India', 'Shared Services'].map(region => (
                      <td key={region} className="p-3 text-center">
                        <Input
                          type="number"
                          value={allocationMatrix[activity]?.[region] || 0}
                          onChange={(e) => updateAllocation(activity, region, parseInt(e.target.value) || 0)}
                          className="bg-[#0F172A] border-[#334155] text-white text-center w-16"
                          min="0"
                        />
                      </td>
                    ))}
                    <td className="p-3 text-center">
                      <span className="text-[#4AE2C9] font-medium">{getActivityTotal(activity)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Completion Screen Component
function CompletionScreen() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-8">
      <div className="relative">
        <div className="w-32 h-32 bg-gradient-to-br from-[#4AE2C9] to-[#3bc7aa] rounded-full flex items-center justify-center">
          <CheckCircle className="w-16 h-16 text-[#0F172A]" />
        </div>
        <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
          <Star className="w-5 h-5 text-white" />
        </div>
      </div>
      
      <div className="space-y-4">
        <h2 className="text-3xl font-bold text-white">Thank You!</h2>
        <p className="text-xl text-gray-300">
          Your information has been successfully captured
        </p>
        <p className="text-gray-400 max-w-md">
          All the project details, strategic context, and FTE allocation data have been recorded. 
          Your Redwood representative will be in touch soon to discuss the next steps in your 
          Record-to-Report transformation journey.
        </p>
      </div>
      
      <div className="bg-[#0F172A] border-[#334155] rounded-lg p-6 max-w-md">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#4AE2C9] rounded-full flex items-center justify-center">
            <Rocket className="w-5 h-5 text-[#0F172A]" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Next Steps</h3>
            <p className="text-sm text-gray-400">What happens next</p>
          </div>
        </div>
        
        <div className="space-y-3 text-sm text-gray-300">
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-[#4AE2C9] rounded-full mt-1.5" />
            <span>Data review and analysis by our team</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-[#4AE2C9] rounded-full mt-1.5" />
            <span>Preparation of your customized assessment</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-[#4AE2C9] rounded-full mt-1.5" />
            <span>Scheduled consultation with your Redwood representative</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2 text-gray-400">
        <Trophy className="w-5 h-5" />
        <span>Ready for your R2R transformation journey</span>
      </div>
    </div>
  )
}

export default function PublicInputFlow() {
  const { projectId: urlProjectId, screen: urlScreen } = useParams()
  const [location, setLocation] = useLocation()
  const [currentScreen, setCurrentScreen] = useState(0)
  const [formData, setFormData] = useState({
    projectKickoff: {},
    strategicContext: {},
    fteAllocation: {},
    inputSubmitted: false
  })
  const { toast } = useToast()
  const { updateInputScreensKickoff, updateInputScreensStrategicContext, updateInputScreensResourceAllocation, valueSummary } = useValueAssessment()
  const { screen: queryScreen, resume, projectId: queryProjectId } = useQueryParams()
  
  // Use projectId from URL params or query params
  const projectId = urlProjectId || queryProjectId
  // Use screen from URL params or query params
  const screen = urlScreen || queryScreen

  // Validate projectId is available
  if (!projectId) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-white">Project ID Required</h2>
          <p className="text-gray-400">Please provide a valid project ID to access the assessment.</p>
          <p className="text-sm text-gray-500">
            Example: /value-assessment/input?projectId=your-project-id&resume=true
          </p>
        </div>
      </div>
    )
  }

  const { data: existingData, isLoading } = useQuery({
    queryKey: ['/api/projects', projectId, 'public-input'],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/public-input`)
      if (!response.ok) throw new Error('Failed to load project data')
      return response.json()
    },
    enabled: !!projectId
  })

  const submitMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`/api/projects/${projectId}/public-input`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to submit data')
      return response.json()
    },
    onSuccess: () => {
      toast({
        title: "Data Submitted Successfully",
        description: "Your information has been recorded. A Redwood representative will contact you soon.",
      })
      setCurrentScreen(3) // Go to completion screen
    },
    onError: (error) => {
      toast({
        title: "Submission Failed",
        description: "Please try again or contact support if the issue persists.",
        variant: "destructive"
      })
    }
  })

  // Screen name to index mapping for deep linking
  const screenMap = {
    'project-kickoff': 0,
    'strategic-context': 1,
    'resource-allocation': 2,
    'review': 3
  }

  useEffect(() => {
    if (existingData) {
      setFormData(existingData)
      if (existingData.inputSubmitted) {
        setCurrentScreen(3) // Go directly to completion screen
      }
    }
  }, [existingData])

  // Handle deep linking and resume logic
  useEffect(() => {
    // Only run if we have data loaded (either valueSummary or existingData)
    const hasData = valueSummary || existingData
    
    if (hasData) {
      if (screen) {
        // Direct deep link to specific screen
        const screenIndex = screenMap[screen as keyof typeof screenMap]
        if (screenIndex !== undefined) {
          console.log('✅ Deep link detected:', screen)
          console.log('✅ Redirecting to screen:', screen)
          setCurrentScreen(screenIndex)
        } else {
          console.warn('⚠️ Invalid screen name:', screen, 'Available screens:', Object.keys(screenMap))
          // Fallback to intro if invalid screen
          setCurrentScreen(0)
        }
      } else if (resume) {
        // Resume from first incomplete screen
        try {
          const nextScreen = getFirstIncompleteScreen(valueSummary)
          const screenIndex = screenMap[nextScreen as keyof typeof screenMap]
          if (screenIndex !== undefined && screenIndex < 3) {
            console.log('✅ Resume requested, detecting incomplete screen:', nextScreen)
            console.log('✅ Redirecting to screen:', nextScreen)
            setCurrentScreen(screenIndex)
          } else {
            console.log('✅ All screens complete, going to review')
            setCurrentScreen(3)
          }
        } catch (error) {
          console.error('❌ Failed to determine incomplete screen:', error)
          // Fallback to first screen on error
          setCurrentScreen(0)
        }
      }
    }
  }, [valueSummary, existingData, screen, resume])

  const handleNext = async () => {
    if (currentScreen < SCREENS.length - 1) {
      // Save current screen data to inputScreens before proceeding
      try {
        switch (currentScreen) {
          case 0: // Project Kickoff
            if (formData.projectKickoff) {
              const kickoffData = {
                clientName: formData.projectKickoff.clientName || '',
                erpSystem: formData.projectKickoff.erpSystems?.join(', ') || '',
                regionCoverage: Object.keys(formData.projectKickoff.regionalFTEs || {}).filter(region => 
                  formData.projectKickoff.regionalFTEs[region] > 0
                ),
                toolsInUse: formData.projectKickoff.strategicGoals?.join(', ') || '',
                notes: `Revenue: ${formData.projectKickoff.revenueBand || 'Not specified'}, Entities: ${formData.projectKickoff.legalEntities || 0}, Close Days: ${formData.projectKickoff.currentCloseDays || 0}→${formData.projectKickoff.targetCloseDays || 0}`
              }
              await updateInputScreensKickoff(kickoffData)
              console.log('✅ Project Kickoff data saved to inputScreens')
            }
            break
          case 1: // Strategic Context
            if (formData.strategicContext) {
              const contextData = {
                priorities: formData.strategicContext.strategicGoals || [],
                painPoints: formData.strategicContext.painPoints || [],
                transformationDrivers: formData.strategicContext.blockers || []
              }
              await updateInputScreensStrategicContext(contextData)
              console.log('✅ Strategic Context data saved to inputScreens')
            }
            break
          case 2: // FTE Allocation
            if (formData.fteAllocation) {
              const resourceData = {
                regionFTEs: formData.fteAllocation.regionalFTEs || {},
                avgCostPerFTE: formData.fteAllocation.fteCostsByRegion || {},
                totalFTEs: Object.values(formData.fteAllocation.regionalFTEs || {}).reduce((sum: number, fte: any) => sum + (fte || 0), 0)
              }
              await updateInputScreensResourceAllocation(resourceData)
              console.log('✅ FTE Allocation data saved to inputScreens')
            }
            break
        }
      } catch (error) {
        console.error('Failed to save screen data to inputScreens:', error)
        toast({
          title: "Save Error",
          description: "Failed to save data. Please try again.",
          variant: "destructive"
        })
        return // Don't proceed if save failed
      }
      
      setCurrentScreen(currentScreen + 1)
    }
  }

  const handlePrevious = () => {
    if (currentScreen > 0) {
      setCurrentScreen(currentScreen - 1)
    }
  }

  const handleSubmit = async () => {
    try {
      // Save final FTE Allocation data to inputScreens before submission
      if (formData.fteAllocation) {
        const resourceData = {
          regionFTEs: formData.fteAllocation.regionalFTEs || {},
          avgCostPerFTE: formData.fteAllocation.fteCostsByRegion || {},
          totalFTEs: Object.values(formData.fteAllocation.regionalFTEs || {}).reduce((sum: number, fte: any) => sum + (fte || 0), 0)
        }
        await updateInputScreensResourceAllocation(resourceData)
        console.log('✅ Final FTE Allocation data saved to inputScreens before submission')
      }
      
      const submissionData = {
        ...formData,
        inputSubmitted: true,
        submissionDate: new Date().toISOString(),
        // Add external completion tracking metadata
        meta: {
          ...formData.meta,
          externalCompleted: true,
          externalCompletedAt: new Date().toISOString(),
          submissionStatus: 'submitted'
        }
      }
      submitMutation.mutate(submissionData)
    } catch (error) {
      console.error('Failed to save final data to inputScreens:', error)
      toast({
        title: "Save Error",
        description: "Failed to save final data. Please try again.",
        variant: "destructive"
      })
    }
  }

  const updateFormData = (section: string, data: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }))
  }

  const isValidToProgress = () => {
    switch (currentScreen) {
      case 0: // Project Kickoff
        return formData.projectKickoff?.clientName && formData.projectKickoff?.revenueBand
      case 1: // Strategic Context
        return formData.strategicContext?.painPoints?.length > 0
      case 2: // FTE Allocation
        return Object.values(formData.fteAllocation || {}).some(activity => 
          Object.values(activity || {}).some(val => val > 0)
        )
      default:
        return true
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#4AE2C9] mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading assessment...</p>
        </div>
      </div>
    )
  }

  const currentScreenData = SCREENS[currentScreen]

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]">
      {/* Header */}
      <div className="border-b border-[#334155]">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-8 w-8 bg-[#4AE2C9] rounded-full flex items-center justify-center">
                <span className="text-[#0F172A] font-bold text-sm">R</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Value Assessment</h1>
                <p className="text-sm text-gray-400">Client Information Collection</p>
              </div>
            </div>
            
            {currentScreen < 3 && (
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-400">
                  Step {currentScreen + 1} of 3
                </div>
                <div className="w-32">
                  <Progress value={((currentScreen + 1) / 3) * 100} className="h-2" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {currentScreen < 3 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">
              {currentScreenData.title}
            </h2>
            <p className="text-gray-400">
              {currentScreenData.description}
            </p>
          </div>
        )}

        {/* Screen Content */}
        <div className="max-w-4xl mx-auto">
          {currentScreen === 0 && (
            <ProjectKickoffForm
              data={formData.projectKickoff}
              onChange={(data) => updateFormData('projectKickoff', data)}
            />
          )}
          
          {currentScreen === 1 && (
            <StrategicContextForm
              data={formData.strategicContext}
              onChange={(data) => updateFormData('strategicContext', data)}
            />
          )}
          
          {currentScreen === 2 && (
            <FTEAllocationForm
              data={formData.fteAllocation}
              onChange={(data) => updateFormData('fteAllocation', data)}
            />
          )}
          
          {currentScreen === 3 && (
            <CompletionScreen />
          )}
        </div>

        {/* Navigation */}
        {currentScreen < 3 && (
          <div className="max-w-4xl mx-auto mt-8 flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentScreen === 0}
              className="border-[#334155] text-gray-300 hover:bg-forestBlack"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            
            {currentScreen === 2 ? (
              <Button
                onClick={handleSubmit}
                disabled={!isValidToProgress() || submitMutation.isPending}
                className="bg-[#4AE2C9] text-[#0F172A] hover:bg-[#3bc7aa]"
              >
                {submitMutation.isPending ? "Submitting..." : "Submit"}
                <CheckCircle className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!isValidToProgress()}
                className="bg-[#4AE2C9] text-[#0F172A] hover:bg-[#3bc7aa]"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}